import {Injectable, OnDestroy} from '@angular/core';
import {BehaviorSubject, Subject, Subscription, timer} from 'rxjs';
import {distinctUntilChanged, filter, take, takeUntil, tap} from 'rxjs/operators';
import {WebSocketConnectionService, WebSocketState} from '../websocket/websocket-connection.service';
import {AuthService} from '../auth/auth.service';
import {TelemetryService} from '../telemetry/telemetry.service';
import {ConfigService} from '../config.service';
import {LoggingService} from '../logging/logging.service';
import {generateCorrelationId, formatLogWithContext} from '../../workers/logging.types';
import {SubscriptionCoordinator} from './subscription-coordinator.service';

/**
 * Connection types available in the application
 */
export enum ConnectionType {
  WEBSOCKET = 'WEBSOCKET'
}

/**
 * Connection state for the application (Main Thread)
 *
 * Note (Phase 6 - FIN-0078):
 * Worker thread now uses unified ConnectionState from connection-state.types.ts
 * with type-safe state machine. Main thread still uses this enum.
 * Future: Unify both to use same ConnectionState + state machine.
 *
 * Difference: Main thread uses FAILED, worker uses ERROR (semantically equivalent)
 */
export enum ConnectionState {
  CONNECTING = 'CONNECTING',
  CONNECTED = 'CONNECTED',
  DISCONNECTED = 'DISCONNECTED',
  RECONNECTING = 'RECONNECTING',
  FAILED = 'FAILED'
}

/**
 * Reason for disconnection
 */
export enum DisconnectReason {
  NORMAL = 'NORMAL',
  ERROR = 'ERROR',
  LOGOUT = 'LOGOUT',
  TIMEOUT = 'TIMEOUT',
  CONNECTION_LOST = 'CONNECTION_LOST',
  AUTH_ERROR = 'AUTH_ERROR'
}

/**
 * Connection status information
 */
export interface ConnectionStatus {
  state: ConnectionState;
  type: ConnectionType;
  timestamp: number;
  disconnectReason?: DisconnectReason;
  wasIntentional?: boolean;
  error?: any;
  reconnectAttempt?: number;
  maxReconnectAttempts?: number;
}

/**
 * Centralized service to manage all connection-related functionality across the application
 * Renamed from ConnectionManagerService to eliminate confusion with worker's WebSocketConnectionManager
 */
@Injectable({
  providedIn: 'root'
})
export class ConnectionOrchestrator implements OnDestroy {
  private destroy$ = new Subject<void>();
  private connectionStatusSubject = new BehaviorSubject<ConnectionStatus>({
    state: ConnectionState.DISCONNECTED,
    type: ConnectionType.WEBSOCKET,
    timestamp: Date.now()
  });

  // Correlation ID for structured logging (links main thread and worker logs)
  private correlationId: string = '';

  // Observable for connection status changes
  public connectionStatus$ = this.connectionStatusSubject.asObservable();

  // Observable filtered for just WebSocket availability
  // FIN-0079 FIX: distinctUntilChanged prevents BehaviorSubject cache replay
  // Without this, new subscriptions immediately receive stale CONNECTED value
  // causing messages to be sent before subscriptions are actually ready
  public websocketAvailable$ = this.connectionStatus$.pipe(
    distinctUntilChanged((a, b) =>
      a.state === b.state &&
      a.type === b.type &&
      a.timestamp === b.timestamp
    ),
    filter(status =>
      status.type === ConnectionType.WEBSOCKET &&
      status.state === ConnectionState.CONNECTED
    )
  );

  // Reconnection control
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 20; // Increased from 5 to 20 for better persistence
  private reconnectDelay = 2000; // 2 seconds, will be increased exponentially
  private reconnectSubscription?: Subscription;

  // Health check related properties
  private healthCheckInterval = 30000; // 30 seconds
  private healthCheckSubscription?: Subscription;

  // Track if we're in the process of disconnecting
  private disconnectInProgress = false;

  // Track if we've ever had an authenticated user
  private hasHadAuthenticatedUser = false;

  // Vite server restart recovery
  private viteRecoveryCheckInterval = 2000; // 2 seconds
  private viteRecoverySubscription?: Subscription;

  constructor(
    private webSocketConnectionService: WebSocketConnectionService,
    private authService: AuthService,
    private telemetryService: TelemetryService,
    private configService: ConfigService,
    private logger: LoggingService,
    private subscriptionCoordinator: SubscriptionCoordinator
  ) {
    this.initialize();
    this.setupVisibilityListener();
  }

  /**
   * Helper method for structured logging
   * Formats logs with correlation ID and state for easy filtering
   *
   * Example output:
   * [ConnectionOrchestrator][conn-12345][CONNECTING] Attempting to connect
   *
   * @param message - The log message
   * @param level - Log level (default: INFO)
   */
  private log(message: string, level: 'DEBUG' | 'INFO' | 'WARN' | 'ERROR' = 'INFO'): void {
    const currentStatus = this.connectionStatusSubject.getValue();

    // Only use structured logging if we have a correlation ID
    if (this.correlationId) {
      // Create minimal context with our local ConnectionState enum
      const context = {
        correlationId: this.correlationId,
        state: currentStatus.state as any,  // Cast to avoid type conflict between worker and service enums
        timestamp: Date.now()
      };

      const formattedMessage = formatLogWithContext('ConnectionOrchestrator', message, context);

      switch (level) {
        case 'DEBUG':
          this.logger.debug('ConnectionOrchestrator', formattedMessage);
          break;
        case 'INFO':
          this.logger.info('ConnectionOrchestrator', formattedMessage);
          break;
        case 'WARN':
          this.logger.warn('ConnectionOrchestrator', formattedMessage);
          break;
        case 'ERROR':
          this.logger.error('ConnectionOrchestrator', formattedMessage, undefined);
          break;
      }
    } else {
      // Fallback to regular logging if no correlation ID yet
      switch (level) {
        case 'DEBUG':
          this.logger.debug('ConnectionOrchestrator', message);
          break;
        case 'INFO':
          this.logger.info('ConnectionOrchestrator', message);
          break;
        case 'WARN':
          this.logger.warn('ConnectionOrchestrator', message);
          break;
        case 'ERROR':
          this.logger.error('ConnectionOrchestrator', message, undefined);
          break;
      }
    }
  }

  /**
   * Get current correlation ID
   */
  public getCorrelationId(): string {
    return this.correlationId;
  }

  /**
   * Initialize the connection manager
   */
  private initialize(): void {
    this.logger.info('ConnectionOrchestrator', 'Initializing');

    // Subscribe to WebSocket connection status
    this.webSocketConnectionService.connected$
      .pipe(takeUntil(this.destroy$))
      .subscribe(connected => {
        if (connected) {
          // FIN-0079: handleConnectionEstablished is now async (waits for subscriptions)
          // Don't await here - let it run independently, errors are handled inside
          this.handleConnectionEstablished(ConnectionType.WEBSOCKET).catch(error => {
            this.log(`❌ Error in handleConnectionEstablished: ${error}`, 'ERROR');
          });
        } else {
          // Only update if we previously thought WebSocket was connected
          // CRITICAL FIX FIN-0052: Don't trigger reconnection during intentional disconnect
          // Race condition: disconnect(NORMAL) → WebSocket closes → connected$ = false
          // If disconnectInProgress is true, this is an intentional disconnect, not CONNECTION_LOST
          const currentStatus = this.connectionStatusSubject.getValue();
          if (currentStatus.type === ConnectionType.WEBSOCKET &&
            currentStatus.state === ConnectionState.CONNECTED &&
            !this.disconnectInProgress) {
            this.handleConnectionLost(ConnectionType.WEBSOCKET, DisconnectReason.CONNECTION_LOST);
          } else if (this.disconnectInProgress) {
            this.logger.debug('ConnectionOrchestrator', 'WebSocket disconnected during intentional disconnect, not triggering reconnection');
          }
        }
      });

    // Map WebSocketConnectionService state to ConnectionManager state
    this.webSocketConnectionService.connectionState$
      .pipe(takeUntil(this.destroy$))
      .subscribe(wsState => {
        this.mapWebSocketStateToConnectionState(wsState);
      });

    // Listen for auth events from the auth service
    this.authService.userAuthInfo$
      .pipe(takeUntil(this.destroy$))
      .subscribe(userInfo => {
        // Skip processing during initial app bootstrap
        try {
          // Check if config is loaded before proceeding
          this.configService.getConfig();

          this.logger.info('ConnectionOrchestrator', `Auth state changed: ${userInfo ? 'User Info Present' : 'NULL'} (hasHadAuthenticatedUser: ${this.hasHadAuthenticatedUser})`);
          if (userInfo) {
            this.logger.info('ConnectionOrchestrator', `User info details - Email: ${userInfo.email}, Token: ${userInfo.token ? 'Present (length: ' + userInfo.token.length + ')' : 'Missing'}`);
          }

          if (userInfo && userInfo.token) {
            // Mark that we've had an authenticated user
            this.hasHadAuthenticatedUser = true;
            
            // User is authenticated - if we're disconnected, try to connect
            const currentStatus = this.connectionStatusSubject.getValue();
            this.logger.info('ConnectionOrchestrator', `Current connection state: ${currentStatus.state}`);
            if (currentStatus.state === ConnectionState.DISCONNECTED) {
              this.logger.info('ConnectionOrchestrator', 'User authenticated, establishing connection');
              this.establishConnection();
            } else {
              this.logger.info('ConnectionOrchestrator', `User authenticated but already in state: ${currentStatus.state}`);
            }
          } else if (userInfo === null) {
            // Only treat as logout if we previously had an authenticated user
            if (this.hasHadAuthenticatedUser) {
              this.logger.warn('ConnectionOrchestrator', 'User auth info is NULL after having authenticated user - treating as logout');
              this.logger.info('ConnectionOrchestrator', 'User logged out, disconnecting');
              this.disconnect(DisconnectReason.LOGOUT);
            } else {
              this.logger.info('ConnectionOrchestrator', 'Ignoring null auth state - no user has been authenticated yet');
            }
          } else if (userInfo && !userInfo.token) {
            this.logger.warn('ConnectionOrchestrator', 'User info present but token missing - cannot establish connection');
          }
        } catch (error) {
          // If config isn't loaded yet, just log and wait
          this.logger.debug('ConnectionOrchestrator', 'Deferring auth status processing until config is loaded', error);
        }
      });

    // Listen for network online/offline events
    window.addEventListener('online', this.handleNetworkOnline.bind(this));
    window.addEventListener('offline', this.handleNetworkOffline.bind(this));
  }

  /**
   * Handle cleanup on service destruction
   */
  ngOnDestroy(): void {
    this.logger.info('ConnectionOrchestrator', 'Destroying service');

    this.destroy$.next();
    this.destroy$.complete();

    // Clean up event listeners
    window.removeEventListener('online', this.handleNetworkOnline.bind(this));
    window.removeEventListener('offline', this.handleNetworkOffline.bind(this));

    // Stop health checks
    this.stopHealthCheck();

    // Stop any reconnection attempts
    this.stopReconnectTimer();

    // Stop Vite recovery checks
    this.stopViteRecoveryCheck();

    // Make sure we're properly disconnected
    const currentStatus = this.connectionStatusSubject.getValue();
    if (currentStatus.state !== ConnectionState.DISCONNECTED) {
      this.disconnect(DisconnectReason.NORMAL);
    }
  }

  /**
   * Map WebSocketState to ConnectionState
   * FIN-0079 FIX: DON'T map CONNECTED state here - handleConnectionEstablished() handles it
   * after waiting for subscriptions to be ready. This prevents 44ms race condition.
   */
  private mapWebSocketStateToConnectionState(wsState: WebSocketState): void {
    let connectionState: ConnectionState | null = null;

    switch (wsState) {
      case WebSocketState.CONNECTED:
        // FIN-0079: Don't emit CONNECTED here! handleConnectionEstablished() will do it
        // after subscriptions are ready. This prevents messages being sent before subscriptions exist.
        return; // Early return - don't update state
      case WebSocketState.CONNECTING:
        connectionState = ConnectionState.CONNECTING;
        break;
      case WebSocketState.RECONNECTING:
        connectionState = ConnectionState.RECONNECTING;
        break;
      case WebSocketState.DISCONNECTED:
        connectionState = ConnectionState.DISCONNECTED;
        break;
      case WebSocketState.ERROR:
        connectionState = ConnectionState.FAILED;
        break;
      default:
        connectionState = ConnectionState.DISCONNECTED;
    }

    // Only update if the connection type is WEBSOCKET
    const currentStatus = this.connectionStatusSubject.getValue();
    if (currentStatus.type === ConnectionType.WEBSOCKET &&
      currentStatus.state !== connectionState) {

      // Update the status
      this.updateConnectionStatus({
        state: connectionState,
        type: ConnectionType.WEBSOCKET,
        timestamp: Date.now()
      });
    }
  }

  /**
   * Get the current connection status
   */
  public getConnectionStatus(): ConnectionStatus {
    return this.connectionStatusSubject.getValue();
  }

  /**
   * Get user identifier (email) from authenticated session.
   * This replaces the old userEmail/clientId logic which was unreliable (sessionStorage cleared on tab close).
   */
  public getUserIdentifier(): string {
    return this.webSocketConnectionService.getUserIdentifier();
  }

  /**
   * Check if WebSocket is currently available as the transport
   */
  public isWebSocketAvailable(): boolean {
    const status = this.connectionStatusSubject.getValue();
    return status.type === ConnectionType.WEBSOCKET && status.state === ConnectionState.CONNECTED;
  }

  /**
   * Establish the WebSocket connection
   * @param preferWebsocket Kept for backward compatibility, now ignored as we always use WebSocket
   */
  public establishConnection(preferWebsocket: boolean = true): void {
    // Generate new correlation ID for this connection lifecycle
    this.correlationId = generateCorrelationId();

    // Check if we have a token before attempting to connect
    if (!this.authService.getAuthToken()) {
      this.log('Token absent - postponing WebSocket connect');
      return;
    }

    const connectionTimerId = `connection-establish-${Date.now()}`;
    this.logger.startTimer(connectionTimerId);

    const currentStatus = this.connectionStatusSubject.getValue();

    // If already connecting or connected, do nothing
    if (currentStatus.state === ConnectionState.CONNECTING ||
      currentStatus.state === ConnectionState.CONNECTED) {
      this.log(`Already ${currentStatus.state.toLowerCase()}`);
      this.logger.endTimer(connectionTimerId, 'ConnectionOrchestrator', 'Connection establishment (already connected)');
      return;
    }

    this.connect();
    this.log('🔌 Connection establishment initiated');
    this.logger.endTimer(connectionTimerId, 'ConnectionOrchestrator', 'Connection establishment initiated');
  }

  /**
   * Connect using WebSocket
   */
  private connect(): void {
    // Verify token presence before attempting to connect
    if (!this.authService.getAuthToken()) {
      this.log('connect() aborted - no JWT present yet', 'DEBUG');
      return;
    }

    this.stopReconnectTimer();

    // Update state to connecting
    this.updateConnectionStatus({
      state: ConnectionState.CONNECTING,
      type: ConnectionType.WEBSOCKET,
      timestamp: Date.now()
    });

    this.connectWebSocket();

    // Start health check after attempting connection
    this.startHealthCheck();
  }

  /**
   * Connect to WebSocket
   */
  private connectWebSocket(): void {
    const wsConnectTimerId = `ws-connect-${Date.now()}`;
    this.logger.startTimer(wsConnectTimerId);
    this.log('🔌 Attempting to connect WebSocket');

    // FIX (FIN-0077): REMOVED short-circuit logic that checked stale state
    // Previously checked if (isConnected()) and returned early.
    // Problem: Main thread had stale state, thought connected, but worker was actually disconnected.
    // Solution: ALWAYS forward connection request to worker. Worker is single source of truth.
    // Worker will handle idempotency (won't reconnect if already connected).

    // FIX (FIN-0078): Pass correlation ID to worker for structured logging
    // This links main thread and worker logs for easier debugging
    this.webSocketConnectionService.connect(this.correlationId)
      .then(() => {
        this.log('✅ WebSocket connected successfully');
        this.logger.endTimer(wsConnectTimerId, 'ConnectionOrchestrator', 'WebSocket connection successful');
        // Connection successful - will be handled by the subscription to connected$
      })
      .catch(error => {
        this.log(`❌ Failed to connect WebSocket: ${error}`, 'ERROR');
        this.telemetryService.trackError('connection_websocket_failed', {error: String(error)});
        this.logger.endTimer(wsConnectTimerId, 'ConnectionOrchestrator', 'WebSocket connection failed');

        this.updateConnectionStatus({
          state: ConnectionState.FAILED,
          type: ConnectionType.WEBSOCKET,
          timestamp: Date.now(),
          error
        });

        // Attempt reconnection
        this.handleConnectionFailure(ConnectionType.WEBSOCKET, error);
      });
  }

  /**
   * Disconnect the current connection with a specific reason
   */
  public disconnect(reason: DisconnectReason = DisconnectReason.NORMAL): void {
    const currentStatus = this.connectionStatusSubject.getValue();

    // Log call stack to understand who's calling disconnect
    this.logger.warn('ConnectionOrchestrator', `disconnect() called with reason: ${reason}`);
    this.logger.warn('ConnectionOrchestrator', 'Call stack:', new Error().stack);

    // If already disconnecting, don't do it again
    if (this.disconnectInProgress) {
      this.logger.warn('ConnectionOrchestrator', `Already disconnecting with reason: ${reason}, skipping duplicate request`);
      return;
    }

    this.disconnectInProgress = true;
    const disconnectTimerId = `disconnect-${Date.now()}`;
    this.logger.startTimer(disconnectTimerId);

    this.stopHealthCheck();
    this.stopReconnectTimer();

    if (this.webSocketConnectionService.isConnected()) {
      this.logger.info('ConnectionOrchestrator', `Disconnecting WebSocket, reason: ${reason}`);

      // Important: If the WebSocketService is available in the global window object,
      // make sure to clean up its progress tracking to prevent memory leaks
      try {
        const webSocketServiceRef = (window as any)['ngWebSocketService'];
        if (webSocketServiceRef && typeof webSocketServiceRef.cleanupProgressTracking === 'function') {
          this.logger.info('ConnectionOrchestrator', 'Cleaning up progress tracking before disconnect');
          webSocketServiceRef.cleanupProgressTracking();
        }
      } catch (error) {
        this.logger.error('ConnectionOrchestrator', 'Error cleaning up progress tracking', error);
      }

      // Now disconnect the connection service
      this.webSocketConnectionService.disconnect();
    }

    // Update connection status
    this.updateConnectionStatus({
      state: ConnectionState.DISCONNECTED,
      type: ConnectionType.WEBSOCKET,
      timestamp: Date.now(),
      disconnectReason: reason,
      wasIntentional: true
    });

    this.telemetryService.trackEvent('connection_disconnected', {
      type: ConnectionType.WEBSOCKET,
      reason
    });

    // Reset disconnect flag after a short delay
    setTimeout(() => {
      this.disconnectInProgress = false;
    }, 500);

    this.logger.endTimer(disconnectTimerId, 'ConnectionOrchestrator', `Disconnection (reason: ${reason})`);
  }

  /**
   * Handle network coming online
   * FIX (FIN-0078): Let worker handle reconnection (single source of truth)
   */
  private handleNetworkOnline(): void {
    this.log('🌐 Network is now online');
    this.telemetryService.trackEvent('network_online');

    // Notify worker that network is online
    // Worker will:
    // 1. Resume health checks
    // 2. Check current state
    // 3. If disconnected/failed: immediately trigger reconnection
    this.webSocketConnectionService.notifyNetworkOnline();

    // REMOVED: Main thread reconnection logic (worker handles it now)
    // This eliminates dual state machines and makes worker the single source of truth
  }

  /**
   * Handle network going offline
   */
  private handleNetworkOffline(): void {
    this.log('📡 Network is now offline');
    this.telemetryService.trackEvent('network_offline');

    // Update status to disconnected
    this.updateConnectionStatus({
      state: ConnectionState.DISCONNECTED,
      type: ConnectionType.WEBSOCKET,
      timestamp: Date.now(),
      disconnectReason: DisconnectReason.CONNECTION_LOST,
      wasIntentional: false
    });

    // Stop health checks and reconnect attempts while offline
    this.stopHealthCheck();
    this.stopReconnectTimer();

    // FIX (FIN-0077): Pause worker health checks to prevent false "stale connection" alarms
    // Previously, worker health check kept running during network offline, causing it to
    // detect "stale connection" after 60s and force unnecessary reconnection.
    // Now we pause worker health check during network outages.
    this.webSocketConnectionService.pauseHealthCheck();
  }

  /**
   * Handle successful connection establishment
   * FIN-0079: Runs all registered initializers before emitting CONNECTED (Callback Registration Pattern)
   */
  private async handleConnectionEstablished(type: ConnectionType): Promise<void> {
    this.log(`✅ Connection established (${type})`);
    console.log('[DEBUG-FIN-0079] handleConnectionEstablished START');

    // Reset reconnect attempts on successful connection
    this.reconnectAttempts = 0;
    console.log('[DEBUG-FIN-0079] Reset reconnect attempts');

    this.stopReconnectTimer();
    console.log('[DEBUG-FIN-0079] Stopped reconnect timer');

    // FIN-0079 FIX: Run all subscription initializers before emitting CONNECTED
    // This eliminates the 44ms race condition where messages are sent before subscriptions exist
    // Uses Callback Registration Pattern to avoid circular dependencies and deadlocks
    console.log('[DEBUG-FIN-0079] About to enter try block for initializers');
    try {
      console.log('[DEBUG-FIN-0079] Inside try block');
      this.log('⏳ Running subscription initializers...');
      const subscriptionTimerId = `subscription-init-${Date.now()}`;
      this.logger.startTimer(subscriptionTimerId);

      console.log('[DEBUG-FIN-0079] About to call runAllInitializers()');
      await this.subscriptionCoordinator.runAllInitializers();
      console.log('[DEBUG-FIN-0079] runAllInitializers() completed');

      this.logger.endTimer(subscriptionTimerId, 'ConnectionOrchestrator', 'All initializers completed');
      this.log('✅ All initializers completed, now emitting CONNECTED');
    } catch (error) {
      // If any initializer fails (e.g., no authenticated user), log but continue
      // The connection is established, just without full application readiness
      console.error('[DEBUG-FIN-0079] EXCEPTION in initializers:', error);
      this.log(`⚠️ Initializer execution failed: ${error}`, 'WARN');
      this.telemetryService.trackEvent('subscription_initializer_error', {error: String(error)});
    }
    console.log('[DEBUG-FIN-0079] After try-catch block');

    // Update connection status - NOW all initializers have completed
    this.updateConnectionStatus({
      state: ConnectionState.CONNECTED,
      type: type,
      timestamp: Date.now()
    });

    this.telemetryService.trackEvent('connection_established', {type});

    // Ensure health check is running
    this.startHealthCheck();
  }

  /**
   * Handle connection loss
   */
  private handleConnectionLost(type: ConnectionType, reason: DisconnectReason): void {
    this.log(`⚠️ Connection lost (${type}), reason: ${reason}`, 'WARN');

    // FIN-0079: Reset subscription readiness since subscriptions are no longer valid
    this.subscriptionCoordinator.resetSubscriptions();

    // Update connection status
    this.updateConnectionStatus({
      state: ConnectionState.DISCONNECTED,
      type: type,
      timestamp: Date.now(),
      disconnectReason: reason,
      wasIntentional: false
    });

    this.telemetryService.trackEvent('connection_lost', {
      type,
      reason
    });

    // Attempt to reconnect
    this.attemptReconnection();
  }

  /**
   * Handle connection failure
   */
  private handleConnectionFailure(type: ConnectionType, error: any): void {
    this.log(`❌ Connection failure (${type}): ${error}`, 'ERROR');
    this.telemetryService.trackError('connection_failure', { type, error: String(error) });
    this.attemptReconnection();
  }

  /**
   * Attempt to reconnect with exponential backoff
   */
  private attemptReconnection(): void {
    // FIN-0066: Don't attempt reconnection if page is backgrounded
    // This prevents blocking HTTP/2 connection pool and delaying other tabs
    if (document.hidden) {
      this.log('Skipping reconnection attempt - page is backgrounded');
      this.updateConnectionStatus({
        state: ConnectionState.DISCONNECTED,
        type: ConnectionType.WEBSOCKET,
        timestamp: Date.now(),
        disconnectReason: DisconnectReason.NORMAL
      });
      return;
    }

    // Stop any existing reconnect timer
    this.stopReconnectTimer();

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.log(`🔴 Maximum reconnection attempts (${this.maxReconnectAttempts}) reached`, 'WARN');

      // Update to failed state
      this.updateConnectionStatus({
        state: ConnectionState.FAILED,
        type: ConnectionType.WEBSOCKET,
        timestamp: Date.now()
      });

      this.telemetryService.trackEvent('connection_reconnect_exhausted', {
        attempts: this.reconnectAttempts,
        type: ConnectionType.WEBSOCKET
      });

      // Start Vite server recovery detection when all reconnection attempts fail
      this.startViteRecoveryCheck();

      return;
    }

    // Calculate backoff delay with jitter
    const baseDelay = this.reconnectDelay * Math.pow(1.5, this.reconnectAttempts);
    const jitterFactor = 0.2; // 20% jitter
    const jitter = baseDelay * jitterFactor * (Math.random() * 2 - 1);
    const delay = Math.min(baseDelay + jitter, 30000); // Cap at 30 seconds

    this.reconnectAttempts++;

    this.log(`🔄 Scheduling reconnection attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${Math.round(delay)}ms`);

    // Update to reconnecting state
    this.updateConnectionStatus({
      state: ConnectionState.RECONNECTING,
      type: ConnectionType.WEBSOCKET,
      timestamp: Date.now(),
      reconnectAttempt: this.reconnectAttempts,
      maxReconnectAttempts: this.maxReconnectAttempts
    });

    // Schedule reconnection
    this.reconnectSubscription = timer(delay)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.log(`🔄 Attempting reconnection (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
        this.connect();
      });
  }

  /**
   * Stop the reconnect timer if active
   */
  private stopReconnectTimer(): void {
    if (this.reconnectSubscription) {
      this.reconnectSubscription.unsubscribe();
      this.reconnectSubscription = undefined;
    }
  }

  /**
   * Start health check for the connection
   */
  private startHealthCheck(): void {
    // Stop any existing health check
    this.stopHealthCheck();

    this.healthCheckSubscription = timer(this.healthCheckInterval, this.healthCheckInterval)
      .pipe(
        takeUntil(this.destroy$),
        tap(() => this.performHealthCheck())
      )
      .subscribe();

    this.logger.debug('ConnectionOrchestrator', `Health check started with interval of ${this.healthCheckInterval}ms`);
  }

  /**
   * Stop the health check if active
   */
  private stopHealthCheck(): void {
    if (this.healthCheckSubscription) {
      this.healthCheckSubscription.unsubscribe();
      this.healthCheckSubscription = undefined;
      this.logger.debug('ConnectionOrchestrator', 'Health check stopped');
    }
  }

  /**
   * Perform a health check on the current connection
   */
  private performHealthCheck(): void {
    const status = this.connectionStatusSubject.getValue();

    if (status.state !== ConnectionState.CONNECTED) {
      return;
    }

    this.logger.debug('ConnectionOrchestrator', 'Performing WebSocket health check');

    // Check if it's actually connected
    if (!this.webSocketConnectionService.isConnected()) {
      this.logger.warn('ConnectionOrchestrator', 'Health check failed: WebSocket reports disconnected state but we thought it was connected');
      this.handleConnectionLost(ConnectionType.WEBSOCKET, DisconnectReason.CONNECTION_LOST);
      return;
    }
  }

  /**
   * Update the connection status and notify subscribers
   */
  private updateConnectionStatus(status: Partial<ConnectionStatus>): void {
    const currentStatus = this.connectionStatusSubject.getValue();
    const newStatus = {
      ...currentStatus,
      ...status,
      timestamp: status.timestamp || Date.now() // Ensure timestamp is always set
    };

    this.connectionStatusSubject.next(newStatus);

    this.logger.info('ConnectionOrchestrator', `Connection status updated: ${newStatus.type} ${newStatus.state}`);

    // Only track telemetry if config is loaded
    try {
      // Check if config is available
      this.configService.getConfig();

      // If we get here, config is loaded, so track the event
      this.telemetryService.trackEvent('connection_status_changed', {
        type: newStatus.type,
        state: newStatus.state,
        previousState: currentStatus.state,
        reason: newStatus.disconnectReason || 'none'
      });
    } catch (error) {
      // Config not loaded yet, skip telemetry
      this.logger.debug('ConnectionOrchestrator', 'Skipping telemetry for connection status update as config is not yet loaded');
    }
  }

  /**
   * Start Vite server restart recovery detection
   */
  private startViteRecoveryCheck(): void {
    // Stop any existing recovery check
    this.stopViteRecoveryCheck();

    this.logger.info('ConnectionOrchestrator', 'Starting Vite server recovery detection');

    this.viteRecoverySubscription = timer(0, this.viteRecoveryCheckInterval)
      .pipe(takeUntil(this.destroy$))
      .subscribe(async () => {
        // Only check for recovery when in FAILED state
        const currentStatus = this.connectionStatusSubject.getValue();
        if (currentStatus.state === ConnectionState.FAILED) {
          try {
            // Simple health check to detect Vite server recovery
            // Use a lightweight endpoint to avoid CORS issues
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 2000); // 2 second timeout

            await fetch('/health', {
              method: 'HEAD',
              cache: 'no-cache',
              signal: controller.signal
            });

            clearTimeout(timeoutId);

            // If we reach here, the server is back online
            this.logger.info('ConnectionOrchestrator', 'Vite server recovered, attempting reconnection');
            this.stopViteRecoveryCheck();

            // Reset reconnection attempts and try to connect
            this.reconnectAttempts = 0;
            this.establishConnection();

          } catch (error) {
            // Server still down, keep checking
            this.logger.debug('ConnectionOrchestrator', 'Vite server still down, continuing recovery checks');
          }
        } else {
          // Connection is no longer failed, stop recovery checks
          this.stopViteRecoveryCheck();
        }
      });
  }

  /**
   * Stop Vite server recovery checks
   */
  private stopViteRecoveryCheck(): void {
    if (this.viteRecoverySubscription) {
      this.viteRecoverySubscription.unsubscribe();
      this.viteRecoverySubscription = undefined;
      this.logger.debug('ConnectionOrchestrator', 'Vite recovery checks stopped');
    }
  }

  /**
   * CRITICAL: Handle page visibility for WebSocket reconnection.
   *
   * When page returns from background:
   * 1. Check WebSocket state
   * 2. Validate token
   * 3. Reconnect if needed
   */
  private setupVisibilityListener(): void {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.handleVisibilityChange();
      }
    });
  }

  /**
   * Handle page becoming visible (user returned from background).
   * CRITICAL FOR MOBILE: Mobile OS kills WebSocket when tab is backgrounded.
   * We MUST reconnect immediately when user returns.
   */
  private async handleVisibilityChange(): Promise<void> {
    this.logger.info('ConnectionOrchestrator', 'Page became visible, checking connection state');

    const currentStatus = this.connectionStatusSubject.getValue();
    const wsConnected = this.webSocketConnectionService.isConnected();

    this.logger.info('ConnectionOrchestrator', `Current state: ${currentStatus.state}, WebSocket connected: ${wsConnected}`);

    // CRITICAL: If WebSocket is disconnected for ANY reason, reconnect immediately
    // This handles mobile background tab scenario where connection is killed by OS
    if (!wsConnected) {
      this.logger.warn('ConnectionOrchestrator', 'WebSocket disconnected while backgrounded, reconnecting immediately');

      // Reset reconnect attempts - this is a fresh start
      this.reconnectAttempts = 0;

      // Reconnect immediately (no delay)
      await this.establishConnection();
      return;
    }

    // If we're somehow in a disconnected/failed state but WS thinks it's connected, fix the inconsistency
    if (currentStatus.state === ConnectionState.DISCONNECTED || currentStatus.state === ConnectionState.FAILED) {
      this.logger.warn('ConnectionOrchestrator', `State mismatch: manager says ${currentStatus.state} but WebSocket says connected, reconnecting`);
      await this.establishConnection();
    }
  }
}
